import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheck, ShieldX, Loader2, FileText, PenLine, MailCheck, CheckCircle2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

/**
 * V37.92 — Page publique de signature électronique de l'attestation
 * nominative DOS DOMIUM par le Maître d'ouvrage.
 *
 * Flux : lien sécurisé (JWT 7 jours) reçu par email → consultation du PDF →
 * demande d'un code OTP 6 chiffres par email (10 min) → signature
 * (nom + qualité + consentement + OTP).
 */
export default function NominativeSignPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const [otpSent, setOtpSent] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signerQuality, setSignerQuality] = useState('');
  const [consent, setConsent] = useState(false);
  const [signing, setSigning] = useState(false);
  const [justSigned, setJustSigned] = useState(null); // { signed_at }

  const fetchInfo = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/domium/public/nominative-sign/${token}`);
      setInfo(res.data);
      if (res.data?.mo_name) setSignerName((prev) => prev || res.data.mo_name);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Lien de signature invalide ou expiré');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  const requestOtp = async () => {
    setOtpSending(true);
    try {
      const res = await axios.post(`${API}/domium/public/nominative-sign/${token}/request-otp`);
      setOtpSent(true);
      toast.success(`Code envoyé à ${res?.data?.sent_to || 'votre adresse email'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi du code");
    } finally {
      setOtpSending(false);
    }
  };

  const submitSign = async () => {
    if (!consent || !signerName.trim() || otpCode.trim().length !== 6) return;
    setSigning(true);
    try {
      const res = await axios.post(`${API}/domium/public/nominative-sign/${token}/sign`, {
        otp_code: otpCode.trim(),
        signer_name: signerName.trim(),
        signer_quality: signerQuality.trim(),
        consent: true,
      });
      setJustSigned({ signed_at: res?.data?.signed_at });
      toast.success('Attestation nominative signée avec succès');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="nominative-sign-loading">
        <div className="flex flex-col items-center gap-3 text-slate-500">
          <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
          <p className="text-sm">Vérification du lien de signature…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="nominative-sign-error">
        <div className="max-w-md w-full bg-white border border-red-200 rounded-2xl shadow-sm p-8 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <ShieldX className="h-7 w-7 text-red-600" />
          </div>
          <h1 className="text-lg font-bold text-slate-900 mb-2">Lien invalide</h1>
          <p className="text-sm text-slate-600">{error}</p>
          <p className="text-xs text-slate-400 mt-4">
            Contactez votre interlocuteur COPPAM pour recevoir un nouveau lien de signature.
          </p>
        </div>
      </div>
    );
  }

  const alreadySigned = info?.signed || !!justSigned;
  const signedAt = justSigned?.signed_at || info?.signed_at;
  const signedBy = justSigned ? signerName : info?.signer_name;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="nominative-sign-page">
      {/* Bandeau */}
      <header className="bg-gradient-to-r from-[#0f2747] to-[#143a63] text-white">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <p className="text-[11px] uppercase tracking-widest text-teal-300 font-semibold mb-1">
            COPPAM — Dispositif DOS DOMIUM
          </p>
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <PenLine className="h-6 w-6 text-teal-300" />
            Signature électronique — Attestation nominative
          </h1>
          <p className="text-sm text-slate-300 mt-1">
            Référence <span className="font-mono text-white">{info?.reference}</span>
            {info?.mo_name ? <> — {info.mo_name}</> : null}
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 grid gap-6 lg:grid-cols-[1fr_380px]">
        {/* Aperçu PDF */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 text-sm font-semibold text-slate-700">
            <FileText className="h-4 w-4 text-teal-600" />
            Attestation nominative de rattachement contractuel
          </div>
          <iframe
            src={`${API}/domium/public/nominative-sign/${token}/pdf`}
            title="Attestation nominative DOS DOMIUM"
            className="w-full border-0"
            style={{ height: '70vh', minHeight: 480 }}
            data-testid="nominative-sign-pdf-iframe"
          />
        </section>

        {/* Panneau de signature */}
        <aside className="space-y-4">
          {alreadySigned ? (
            <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-6 text-center" data-testid="nominative-sign-success">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <h2 className="text-lg font-bold text-slate-900 mb-1">Attestation signée</h2>
              <p className="text-sm text-slate-600">
                Signée électroniquement{signedBy ? <> par <strong>{signedBy}</strong></> : null}
                <br />le {formatDateTime(signedAt)}
              </p>
              <div className="mt-4 text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 flex items-center gap-2 justify-center">
                <ShieldCheck className="h-4 w-4 shrink-0" />
                Signature horodatée et tracée dans le registre d'audit du dossier
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <PenLine className="h-4 w-4 text-teal-600" /> Signer en tant que Maître d'ouvrage
              </h2>

              {/* Étape 1 : OTP */}
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-3">
                <p className="text-xs text-slate-600">
                  <strong>Étape 1 —</strong> Recevez un code de vérification à usage unique sur
                  votre adresse email <span className="font-mono">{info?.to_email_masked}</span> (validité 10 minutes).
                </p>
                <Button
                  onClick={requestOtp}
                  disabled={otpSending}
                  variant={otpSent ? 'outline' : 'default'}
                  className={otpSent ? 'w-full border-teal-300 text-teal-700' : 'w-full bg-teal-600 hover:bg-teal-700 text-white'}
                  data-testid="nominative-sign-request-otp-btn"
                >
                  {otpSending
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Envoi du code…</>
                    : otpSent
                      ? <><MailCheck className="h-4 w-4 mr-2" />Renvoyer un code</>
                      : <><MailCheck className="h-4 w-4 mr-2" />Recevoir mon code par email</>}
                </Button>
                {otpSent && (
                  <p className="text-[11px] text-emerald-700 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Code envoyé — vérifiez votre boîte de réception (et vos spams)
                  </p>
                )}
              </div>

              {/* Étape 2 : formulaire */}
              <div className="space-y-3">
                <p className="text-xs text-slate-600"><strong>Étape 2 —</strong> Complétez puis signez.</p>
                <div className="space-y-1">
                  <Label htmlFor="signer-name" className="text-sm">Nom du signataire <span className="text-red-500">*</span></Label>
                  <Input
                    id="signer-name"
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    placeholder="Prénom NOM ou raison sociale"
                    data-testid="nominative-sign-name-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="signer-quality" className="text-sm">Qualité (optionnel)</Label>
                  <Input
                    id="signer-quality"
                    value={signerQuality}
                    onChange={(e) => setSignerQuality(e.target.value)}
                    placeholder="Gérant, représentant légal…"
                    data-testid="nominative-sign-quality-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="otp-code" className="text-sm">Code reçu par email <span className="text-red-500">*</span></Label>
                  <Input
                    id="otp-code"
                    inputMode="numeric"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="123456"
                    className="font-mono tracking-[0.4em] text-center text-lg"
                    data-testid="nominative-sign-otp-input"
                  />
                </div>
                <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer select-none">
                  <Checkbox
                    checked={consent}
                    onCheckedChange={(v) => setConsent(!!v)}
                    className="mt-0.5"
                    data-testid="nominative-sign-consent-checkbox"
                  />
                  <span>
                    Je reconnais avoir pris connaissance de l'attestation nominative ci-contre et je consens à la
                    signer électroniquement en qualité de maître d'ouvrage. Cette signature est horodatée et tracée.
                  </span>
                </label>
                <Button
                  onClick={submitSign}
                  disabled={!consent || !signerName.trim() || otpCode.trim().length !== 6 || signing}
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
                  data-testid="nominative-sign-submit-btn"
                >
                  {signing
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Signature en cours…</>
                    : <><PenLine className="h-4 w-4 mr-2" />Signer électroniquement</>}
                </Button>
              </div>
            </div>
          )}

          <div className="text-[11px] text-slate-400 px-2">
            Lien personnel valable jusqu'au {formatDateTime(info?.expires_at)}.
            <br />COPPAM — F.O.G.E.D.O.M — FOGEDOM-SCIC — Dispositif DOS DOMIUM.
          </div>
        </aside>
      </main>
    </div>
  );
}
